/**
 * V2-only E-Learning image mapping.
 * V1 continues using legacy paths from API/DB unchanged.
 *
 * Upload redesigned assets to:
 *   {SERVER_URL_IMG}/media/elearning/images/v2/
 * Local dev: place files in portal/public/media/elearning/images/v2/
 */
import configData from '../../../config';

export const V2_IMAGE_ROOT = '/media/elearning/images/v2/';

function legacyImgUrl(path) {
    if (!path) return '';
    if (String(path).search('http') !== -1) return path;
    const base = configData.SERVER_URL_IMG || '';
    const normalized = String(path).startsWith('/') ? path.slice(1) : path;
    return `${base}${normalized}`;
}

/**
 * Program heroes + dashboard strip (no API image).
 * Shipped with the portal under public/media/dashboard-v2/ (same pattern as Goals).
 * Legacy elearning/images/v2/ names remain valid upload aliases if preferred on CDN.
 */
export const V2_PROGRAM_HEROES = {
    breakthrough:
        '/media/dashboard-v2/dashboard-elearning-breakthrough-v2.jpg',
    path: '/media/dashboard-v2/dashboard-elearning-path-v2.jpg',
    additionalStrip:
        '/media/dashboard-v2/dashboard-elearning-additional-programs-v2.jpg',
};

/**
 * Additional course covers by stable DB id (preferred).
 * @type {Record<number, string>}
 */
export const V2_COURSE_IMAGES_BY_ID = {
    1: `${V2_IMAGE_ROOT}green-gap-v2.jpg`,
    2: `${V2_IMAGE_ROOT}body-by-design-v2.jpg`,
    3: `${V2_IMAGE_ROOT}miracle-mindset-v2.jpg`,
    4: `${V2_IMAGE_ROOT}23-strong-phase-1-v2.jpg`,
    5: `${V2_IMAGE_ROOT}23-strong-phase-2-v2.jpg`,
    6: `${V2_IMAGE_ROOT}23-strong-phase-3-v2.jpg`,
    7: `${V2_IMAGE_ROOT}23-strong-phase-4-v2.jpg`,
    8: `${V2_IMAGE_ROOT}23-strong-phase-5-v2.jpg`,
    9: `${V2_IMAGE_ROOT}23-strong-phase-6-v2.jpg`,
    10: `${V2_IMAGE_ROOT}23-strong-phase-7-v2.jpg`,
    11: `${V2_IMAGE_ROOT}23-strong-phase-8-v2.jpg`,
    1007: `${V2_IMAGE_ROOT}who-are-you-v2.jpg`,
    1009: `${V2_IMAGE_ROOT}mind-body-coach-v2.jpg`,
};

/** Fallback when course id differs between environments */
export const V2_COURSE_IMAGES_BY_TITLE = {
    'Green Gap': V2_COURSE_IMAGES_BY_ID[1],
    'Oola Body by Design': V2_COURSE_IMAGES_BY_ID[2],
    'The Miracle Mindset': V2_COURSE_IMAGES_BY_ID[3],
    '23 Strong - Phase 1': V2_COURSE_IMAGES_BY_ID[4],
    '23 Strong - Phase 2': V2_COURSE_IMAGES_BY_ID[5],
    '23 Strong - Phase 3': V2_COURSE_IMAGES_BY_ID[6],
    '23 Strong - Phase 4': V2_COURSE_IMAGES_BY_ID[7],
    '23 Strong - Phase 5': V2_COURSE_IMAGES_BY_ID[8],
    '23 Strong - Phase 6': V2_COURSE_IMAGES_BY_ID[9],
    '23 Strong - Phase 7': V2_COURSE_IMAGES_BY_ID[10],
    '23 Strong - Phase 8': V2_COURSE_IMAGES_BY_ID[11],
    'Who Are You?': V2_COURSE_IMAGES_BY_ID[1007],
    'Mind & Body Coach Certification': V2_COURSE_IMAGES_BY_ID[1009],
};

const moduleV2 = (slug) => `${V2_IMAGE_ROOT}modules/${slug}-v2.jpg`;

/**
 * User-module API ids are per-enrollment (e.g. 39364) — do NOT key overrides by those.
 * Use title (and optional legacy filename) so V1 imagePath stays untouched as fallback.
 */
export const V2_MODULE_IMAGES_BY_ID = {};

/**
 * Breakthrough + Additional-course module covers (V2-only).
 * Keys must match API `title` exactly.
 */
export const V2_MODULE_IMAGES_BY_TITLE = {
    // —— My Breakthrough (full lesson pool) ——
    'Grow Your Fitness to Find Balance': moduleV2('grow-your-fitness-balance'),
    'Grow Your Finance to Find Balance': moduleV2('grow-your-finance-balance'),
    'Grow Your Family to Find Balance': moduleV2('grow-your-family-balance'),
    'Grow Your Field to Find Balance': moduleV2('grow-your-field-balance'),
    'Grow Your Faith to Find Balance': moduleV2('grow-your-faith-balance'),
    'Grow Your Friends to Find Balance': moduleV2('grow-your-friends-balance'),
    'Grow Your Fun to Find Balance': moduleV2('grow-your-fun-balance'),
    'Finding Balance Through Commitment': moduleV2(
        'finding-balance-through-commitment'
    ),
    'Committing to Your OolaLife': moduleV2('committing-to-your-oolalife'),
    'Taking Daily Action': moduleV2('taking-daily-action'),
    'The Power of Community': moduleV2('the-power-of-community'),
    'Clear the Clutter': moduleV2('clear-the-clutter'),
    "Removing What's Blocking You": moduleV2('removing-whats-blocking-you'),
    'Overcoming Fear': moduleV2('overcoming-fear'),
    'Releasing Guilt': moduleV2('releasing-guilt'),
    'Letting Go of Anger': moduleV2('letting-go-of-anger'),
    'You Are Worthy': moduleV2('you-are-worthy'),
    'Overcome Laziness': moduleV2('overcome-laziness'),
    'Watch Your Own Bobber': moduleV2('watch-your-own-bobber'),
    'ReFocus on what matters': moduleV2('refocus-on-what-matters'),
    'Accelerating Your Results': moduleV2('accelerating-your-results'),
    'Year of Gratitude': moduleV2('year-of-gratitude'),
    'Year of Love': moduleV2('year-of-love'),
    'Year of Discipline': moduleV2('year-of-discipline'),
    'Year of Integrity': moduleV2('year-of-integrity'),
    'Year of Passion': moduleV2('year-of-passion'),
    'Year of Humility': moduleV2('year-of-humility'),
    'Year of Wisdom': moduleV2('year-of-wisdom'),
    'Tear It Up and Dream Bigger': moduleV2('tear-it-up-and-dream-bigger'),
    'Building an Abundant Mindset': moduleV2('building-an-abundant-mindset'),
    'Building Confidence': moduleV2('building-confidence'),
    'Humble Confidence': moduleV2('humble-confidence'),
    'The Power of Your Thoughts And Actions': moduleV2(
        'power-of-your-thoughts-and-actions'
    ),

    // —— Green Gap ——
    'Identify your Money Mindset': moduleV2('identify-your-money-mindset'),
    'I Hate Debt Chart': moduleV2('i-hate-debt-chart'),
    'Establish your Financial Flag': moduleV2('establish-your-financial-flag'),
    '90-Day Look-Back Budget': moduleV2('90-day-look-back-budget'),
    'Your Oola life': moduleV2('your-oola-life'),
    'Complete Emergency Fund': moduleV2('complete-emergency-fund'),
    'Budgeting on an Irregular': moduleV2('budgeting-on-an-irregular'),
    'Identify the Gap': moduleV2('identify-the-gap'),
    'Maximize the Gap': moduleV2('maximize-the-gap'),
    'Apportion the Gap': moduleV2('apportion-the-gap'),
    'Monthly Money Habit': moduleV2('monthly-money-habit'),
};

/**
 * Creative brief + legacy reference for manual design / upload.
 * (Not used at runtime — documentation for asset production.)
 */
export const V2_IMAGE_ASSET_MANIFEST = [
    {
        program: 'Green Gap',
        courseId: 1,
        legacyPath: '/media/elearning/images/Green Gap course.png',
        v2Path: V2_COURSE_IMAGES_BY_ID[1],
        direction:
            'Deep green and charcoal financial freedom; subtle growth symbolism; calm confidence; minimal text.',
    },
    {
        program: 'Oola Body by Design',
        courseId: 2,
        legacyPath: '/media/elearning/images/Oola Body By Design.png',
        v2Path: V2_COURSE_IMAGES_BY_ID[2],
        direction:
            'Body-positive wellness transformation; natural light; soft green/teal; not aggressive gym.',
    },
    {
        program: 'The Miracle Mindset',
        courseId: 3,
        legacyPath: '/media/elearning/images/MiracleMindsetCover.jpg',
        v2Path: V2_COURSE_IMAGES_BY_ID[3],
        direction:
            'Warm sunrise reflection; journaling/peace; soft gold/rose; emotional but refined.',
    },
    {
        program: '23 Strong (Phases 1–8)',
        courseId: '4–11',
        legacyPath: '/media/elearning/images/Phase N cover.jpg',
        v2Path: `${V2_IMAGE_ROOT}23-strong-phase-N-v2.jpg`,
        direction:
            'Discipline and resilience; mountain trail/endurance; charcoal/navy with warm orange accent.',
    },
    {
        program: 'Who Are You?',
        courseId: 1007,
        legacyPath: '/media/elearning/images/Who Are You_ COVER-long.jpg',
        v2Path: V2_COURSE_IMAGES_BY_ID[1007],
        direction:
            'Identity and self-discovery; thoughtful portrait or reflection; orange/gold accent.',
    },
    {
        program: 'Mind & Body Coach Certification',
        courseId: 1009,
        legacyPath: '/media/elearning/images/M&B-Coach-Cover.jpg',
        v2Path: V2_COURSE_IMAGES_BY_ID[1009],
        direction:
            'Professional coaching certification; navy/charcoal with gold/teal; credible mentor energy.',
    },
    {
        program: 'My Breakthrough Experience (hero)',
        courseId: null,
        legacyPath: '(Unsplash fallback in programConfig)',
        v2Path: V2_PROGRAM_HEROES.breakthrough,
        direction:
            'Person beginning a transformation — walking forward on trail/steps; personal human scale; warm cinematic dawn; no text.',
    },
    {
        program: 'My Path (hero)',
        courseId: null,
        legacyPath: '(Unsplash fallback in programConfig)',
        v2Path: V2_PROGRAM_HEROES.path,
        direction:
            'Expansive next chapter — long path/valley into distant horizon; broader than Breakthrough; 9-month journey mood; no text.',
    },
    {
        program: 'Additional Programs (dashboard strip)',
        courseId: null,
        legacyPath: '(Unsplash fallback in programConfig)',
        v2Path: V2_PROGRAM_HEROES.additionalStrip,
        direction:
            'Premium learning library — dark desk, journal, tablet/laptop blurred UI, warm lamp, soft mountain mood through window; explore programs; no logos/text clutter.',
    },
    {
        program: 'Module: Grow Your Fitness to Find Balance',
        courseId: null,
        legacyPath: '(API module.imagePath)',
        v2Path: V2_MODULE_IMAGES_BY_TITLE[
            'Grow Your Fitness to Find Balance'
        ],
        direction:
            'Person walking/lightly running on mountain path at sunrise; calm wellness; balance; healthy rhythm; no text.',
    },
    {
        program: 'Module: Finding Balance Through Commitment',
        courseId: null,
        legacyPath: '(API module.imagePath)',
        v2Path: V2_MODULE_IMAGES_BY_TITLE[
            'Finding Balance Through Commitment'
        ],
        direction:
            'Focused journaling/planning in quiet premium space; discipline and clarity; no text.',
    },
    {
        program: 'Module: Taking Daily Action',
        courseId: null,
        legacyPath: '(API module.imagePath)',
        v2Path: V2_MODULE_IMAGES_BY_TITLE['Taking Daily Action'],
        direction:
            'Starting the day with checklist/action plan; warm light; intentional progress; no text.',
    },
];

function slugifyFilename(path) {
    if (!path) return '';
    const base = String(path).split('/').pop() || '';
    return base
        .replace(/\.[a-z0-9]+$/i, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

/**
 * Resolve V2 relative path for a program/course/hero/module.
 */
export function getV2ImageRelativePath({
    courseId,
    moduleId,
    title,
    legacyPath,
    kind = 'course',
} = {}) {
    if (kind === 'breakthrough-hero') return V2_PROGRAM_HEROES.breakthrough;
    if (kind === 'path-hero') return V2_PROGRAM_HEROES.path;
    if (kind === 'additional-strip') return V2_PROGRAM_HEROES.additionalStrip;

    if (kind === 'module') {
        if (moduleId != null && V2_MODULE_IMAGES_BY_ID[moduleId]) {
            return V2_MODULE_IMAGES_BY_ID[moduleId];
        }
        if (title && V2_MODULE_IMAGES_BY_TITLE[title]) {
            return V2_MODULE_IMAGES_BY_TITLE[title];
        }
        // Optional convention: slug from legacy filename if designer uploaded matching file
        if (legacyPath) {
            const slug = slugifyFilename(legacyPath);
            if (slug) return `${V2_IMAGE_ROOT}modules/${slug}-v2.jpg`;
        }
        return null;
    }

    if (courseId != null && V2_COURSE_IMAGES_BY_ID[courseId]) {
        return V2_COURSE_IMAGES_BY_ID[courseId];
    }
    if (title && V2_COURSE_IMAGES_BY_TITLE[title]) {
        return V2_COURSE_IMAGES_BY_TITLE[title];
    }
    return null;
}

/**
 * Build absolute URL for a V2 asset.
 * Dashboard-v2 public assets ship with the portal (PUBLIC_URL), like Goals mountain.
 * Other elearning/images/v2 assets: same-origin in dev; SERVER_URL_IMG in prod.
 */
export function buildV2AssetUrl(relativePath) {
    if (!relativePath) return '';
    if (/^https?:\/\//i.test(relativePath)) return relativePath;

    const normalized = relativePath.startsWith('/')
        ? relativePath
        : `/${relativePath}`;

    if (normalized.startsWith('/media/dashboard-v2/')) {
        return `${process.env.PUBLIC_URL || ''}${normalized}`;
    }

    // V2 e-learning assets ship with the portal (public/media/elearning/images/v2)
    if (normalized.startsWith('/media/elearning/images/v2/')) {
        return `${process.env.PUBLIC_URL || ''}${normalized}`;
    }

    if (
        typeof window !== 'undefined' &&
        process.env.NODE_ENV === 'development'
    ) {
        return `${window.location.origin}${normalized}`;
    }

    return legacyImgUrl(normalized);
}

/**
 * Returns primary (V2) and fallback (legacy) URLs for safe rendering.
 */
export function resolveElearningImagePair({
    courseId,
    moduleId,
    title,
    legacyPath,
    kind = 'course',
    fallbackUrl,
} = {}) {
    const v2Relative = getV2ImageRelativePath({
        courseId,
        moduleId,
        title,
        legacyPath,
        kind,
    });
    const fallback =
        legacyPath != null && legacyPath !== ''
            ? legacyImgUrl(legacyPath)
            : fallbackUrl || '';

    if (v2Relative) {
        return {
            src: buildV2AssetUrl(v2Relative),
            fallbackSrc: fallback,
            v2Relative,
            isV2: true,
        };
    }

    return {
        src: fallback,
        fallbackSrc: fallback,
        v2Relative: null,
        isV2: false,
    };
}

/** Hero image for programConfig empty states / strip */
export function getV2ProgramHeroUrl(programId) {
    const kindMap = {
        breakthrough: 'breakthrough-hero',
        path: 'path-hero',
        additional: 'additional-strip',
    };
    const kind = kindMap[programId];
    if (!kind) return null;
    const relative = getV2ImageRelativePath({ kind });
    return relative ? buildV2AssetUrl(relative) : null;
}
